/**
 * Request bodies, validated on the server and reused by the web forms.
 */
import { z } from "zod";
import { canonicalDestination, isAllowedDestination, LINK_CHANNELS, LINK_FALLBACK_URL } from "./links";

const optText = z
  .string()
  .trim()
  .max(5000)
  .nullish()
  .transform((v) => (v ? v : null));
const optShort = z
  .string()
  .trim()
  .max(300)
  .nullish()
  .transform((v) => (v ? v : null));
const optId = z.string().min(1).nullish().transform((v) => v ?? null);
const isoDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, "Use YYYY-MM-DD");
const optDate = isoDate.nullish().transform((v) => v ?? null);
const optEmail = z
  .string()
  .trim()
  .toLowerCase()
  .nullish()
  .transform((v) => (v ? v : null))
  .refine((v) => v === null || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v), "Enter a valid email address");

export const frequencySchema = z.enum(["annual", "quarterly"]);

export function normaliseDomain(input: string | null | undefined): string | null {
  if (!input) return null;
  let d = input.trim().toLowerCase();
  d = d.replace(/^https?:\/\//, "").replace(/^www\./, "");
  d = d.split("/")[0]!.split("?")[0]!;
  if (d.includes("@")) d = d.split("@")[1]!;
  return d || null;
}

/* -------- Registry -------- */

/** Xero's contact ids are GUIDs; they're kept lower case so the same contact can't be linked twice. */
const XERO_GUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const xeroContactIds = z
  .array(z.string().trim().min(1).max(100))
  .max(20, "20 Xero contacts at most")
  .transform((ids) => [...new Set(ids.map((id) => (XERO_GUID.test(id) ? id.toLowerCase() : id)))]);

/** Every region a provider or partner operates in. */
const regionList = z
  .array(z.string().trim().min(1).max(100))
  .max(20, "20 regions at most")
  .transform((rs) => [...new Set(rs)]);

export const organisationInput = z.object({
  name: z.string().trim().min(1, "Name is required").max(200),
  domain: optShort.transform((v) => normaliseDomain(v)),
  website: optShort,
  phone: optShort,
  email: optEmail,
  addressLine: optShort,
  town: optShort,
  postcode: optShort,
  regions: regionList.default([]),
  localAuthority: optShort,
  providerTypeId: optId,
  siteCount: z.coerce.number().int().min(1, "At least one site").max(5000),
  ofstedUrn: optShort,
  ownerId: optId,
  notes: optText,
  /** The Xero contacts the customer is billed through: one per registered entity. */
  xeroContactIds: xeroContactIds.default([]),
  bertTenantId: optShort,
  tagIds: z.array(z.string()).default([]),
  engaged: z.boolean().default(false),
});
export type OrganisationInput = z.input<typeof organisationInput>;

/*
 * PATCH schemas. zod 4 still applies .default() inside .partial(), which would
 * silently reset fields a PATCH didn't mention, so defaulted fields are
 * re-declared as plain optionals.
 */
export const organisationPatch = organisationInput
  .omit({ tagIds: true, engaged: true, xeroContactIds: true, regions: true })
  .partial()
  .extend({
    tagIds: z.array(z.string()).optional(),
    engaged: z.boolean().optional(),
    xeroContactIds: xeroContactIds.optional(),
    regions: regionList.optional(),
  });
export type OrganisationPatch = z.input<typeof organisationPatch>;

const suppressionChannel = z.enum(["email", "phone", "in_person", "web_form", "reply", "bounce", "complaint", "other"]);

export const suppressionInput = z
  .object({
    suppressed: z.boolean(),
    reason: optShort,
    /** How they asked: required when suppressing. */
    channel: suppressionChannel.nullish().transform((v) => v ?? null),
  })
  .refine((v) => !v.suppressed || v.channel, { message: "Say how they asked", path: ["channel"] });

export const bulkOrganisationInput = z.object({
  ids: z.array(z.string()).min(1).max(5000),
  addTagIds: z.array(z.string()).default([]),
  removeTagIds: z.array(z.string()).default([]),
  ownerId: z.string().nullish(),
});

/** Move organisations filed in the wrong list: prospects to partners, or back. */
export const registryMoveInput = z.object({
  ids: z.array(z.string().min(1)).min(1).max(5000),
  to: z.enum(["partner", "customer"]),
  /** Partners: the stage they go into (the first open stage if not given). */
  stageId: z.string().min(1).optional(),
  /** Partners: their partner types. */
  typeIds: z.array(z.string()).default([]),
});

/* -------- Contacts -------- */

export const contactInput = z.object({
  firstName: z.string().trim().min(1, "First name is required").max(100),
  lastName: z.string().trim().max(100).default(""),
  email: optEmail,
  phone: optShort,
  mobile: optShort,
  jobTitle: optShort,
  linkedinUrl: optShort,
  notes: optText,
});
export type ContactInput = z.input<typeof contactInput>;

export const contactPatch = contactInput
  .omit({ lastName: true })
  .partial()
  .extend({ lastName: z.string().trim().max(100).optional() });

export const contactCreateInput = contactInput.extend({
  organisationId: optId,
  role: optShort,
  isKeyPerson: z.boolean().default(false),
});
export type ContactCreateInput = z.input<typeof contactCreateInput>;

export const relationshipInput = z.object({
  organisationId: z.string().min(1),
  role: optShort,
  isKeyPerson: z.boolean().default(false),
  isPrimary: z.boolean().default(false),
  startedAt: optDate,
  /** End the contact's current relationship(s) at the same time: a move, not a duplicate. */
  endCurrent: z.boolean().default(true),
});
export type RelationshipInput = z.input<typeof relationshipInput>;

export const relationshipPatch = z.object({
  role: optShort.optional(),
  isKeyPerson: z.boolean().optional(),
  isPrimary: z.boolean().optional(),
  startedAt: optDate.optional(),
  endedAt: optDate.optional(),
  endReason: optShort.optional(),
});

export const contactPreferencesInput = z.object({
  topicIds: z.array(z.string()),
  source: z.enum(["contact", "team"]).default("team"),
});

/** Bulk topic changes from the Contacts registry. Opted-out contacts are never given topics. */
export const bulkContactInput = z.object({
  ids: z.array(z.string()).min(1).max(5000),
  addTopicIds: z.array(z.string()).default([]),
  removeTopicIds: z.array(z.string()).default([]),
});

export const contactConsentInput = z
  .object({
    optedOut: z.boolean().optional(),
    suppressed: z.boolean().optional(),
    reason: optShort,
    source: z.enum(["contact", "team"]).default("team"),
    /** How they asked: required when suppressing. */
    channel: suppressionChannel.nullish().transform((v) => v ?? null),
  })
  .refine((v) => v.suppressed !== true || v.channel, { message: "Say how they asked", path: ["channel"] });

/* -------- Deals -------- */

export const lineItemInput = z.object({
  id: z.string().optional(),
  productId: optId,
  description: z.string().trim().min(1).max(300),
  quantity: z.coerce.number().min(0).max(100000),
  unitPrice: z.coerce.number().min(0).max(10_000_000),
});

export const freeKindSchema = z.enum(["pilot", "trial", "onboarding"]);

const discountFields = {
  discountPct: z.coerce.number().gt(0, "More than 0%").max(100, "100% at most").nullish().transform((v) => v ?? null),
  discountFrom: optDate,
  discountUntil: optDate,
};

export const dealCreateInput = z.object({
  organisationId: z.string().min(1),
  kind: z.enum(["new_business", "expansion"]).default("new_business"),
  name: z.string().trim().min(1, "Name the deal").max(200),
  ownerId: optId,
  stageId: optId,
  sites: z.coerce.number().int().min(1).max(5000),
  /** Contract term: sets the price. */
  frequency: frequencySchema.default("annual"),
  /** Invoicing, when it differs from the term (annual contracts invoiced quarterly). */
  invoiceSchedule: frequencySchema.nullish(),
  /** Where the lead came from (optional), the campaign behind it, and any detail. */
  leadSourceId: optId,
  leadCampaignId: optId,
  leadDetail: optShort,
  expectedCloseDate: optDate,
  /** A free period: £0 until this last free day, then the deal's value. */
  pilotEndsOn: optDate,
  pilotKind: freeKindSchema.default("pilot"),
  /** % off the licence for a period: from `discountFrom` (or when paying starts) to `discountUntil`. */
  ...discountFields,
  notes: optText,
  lineItems: z.array(lineItemInput).default([]),
});
export type DealCreateInput = z.input<typeof dealCreateInput>;

export const dealPatch = z.object({
  name: z.string().trim().min(1).max(200).optional(),
  ownerId: optId.optional(),
  sites: z.coerce.number().int().min(1).max(5000).optional(),
  frequency: frequencySchema.optional(),
  invoiceSchedule: frequencySchema.nullable().optional(),
  leadSourceId: optId.optional(),
  leadCampaignId: optId.optional(),
  leadDetail: optShort.optional(),
  expectedCloseDate: optDate.optional(),
  /** Set, move or (null) remove the free period's last day. */
  pilotEndsOn: optDate.optional(),
  pilotKind: freeKindSchema.optional(),
  /** Set or change the discount; discountPct null removes it. */
  discountPct: discountFields.discountPct.optional(),
  discountFrom: optDate.optional(),
  discountUntil: optDate.optional(),
  notes: optText.optional(),
  lineItems: z.array(lineItemInput).optional(),
});
export type DealPatch = z.input<typeof dealPatch>;

/** A won free pilot starts paying: from today unless another day (not in the future) is given. */
export const pilotConvertInput = z.object({ convertedOn: isoDate.optional() });

/** Admin only: when a deal really opened, and was won or lost (deals entered after the fact, or corrected). */
export const dealDatesInput = z.object({
  openedOn: isoDate.optional(),
  wonOn: isoDate.optional(),
  lostOn: isoDate.optional(),
});

/** What brought a lost or dormant deal back. */
export const reengagementInput = z.object({
  campaignId: optId,
  sourceId: optId,
  note: optShort,
});

export const dealMoveInput = z.object({
  stageId: z.string().min(1),
  lostReasonId: optId,
  lostNote: optText,
  /** When reopening a lost deal: what brought it back (optional). */
  reengagement: reengagementInput.optional(),
});
export type DealMoveInput = z.input<typeof dealMoveInput>;

/** Expansion deals skip the sales pipeline: they are simply open, won or lost. */
export const expansionStatusInput = z.object({
  status: z.enum(["open", "won", "lost"]),
  lostReasonId: optId,
  lostNote: optText,
});

/* -------- Activities -------- */

export const activityInput = z.object({
  organisationId: z.string().min(1),
  dealId: optId,
  contactId: optId,
  typeId: z.string().min(1),
  occurredAt: z.string().datetime({ offset: true }).or(z.string().datetime()),
  durationMinutes: z.coerce.number().int().min(0).max(24 * 60).nullish().transform((v) => v ?? null),
  subject: z.string().trim().min(1, "Add a short summary").max(300),
  body: optText,
  /** "no_thanks" suppresses the contact; "bounced" flags their address. Both need a contact. */
  replyOutcome: z.enum(["interested", "not_now", "no_thanks", "wrong_person", "bounced"]).nullish().transform((v) => v ?? null),
});
export type ActivityInput = z.input<typeof activityInput>;

/* -------- Planned activities and the calendar -------- */

const isoDateTime = z.string().datetime({ offset: true }).or(z.string().datetime());
/** Admin only: a corrected date and time for a logged activity or a deal log entry. */
export const redateInput = z.object({ at: isoDateTime });

export const plannedInput = z.object({
  organisationId: z.string().min(1, "Pick who it's with"),
  dealId: optId,
  contactId: optId,
  typeId: z.string().min(1, "Pick a type"),
  title: z.string().trim().min(1, "Add a title").max(300),
  notes: optText,
  startAt: isoDateTime,
  durationMinutes: z.coerce.number().int().min(0).max(24 * 60).default(30),
  ownerId: optId,
  inviteContact: z.boolean().default(false),
  reminderMinutes: z.coerce.number().int().min(0).max(4 * 7 * 24 * 60).nullish().transform((v) => v ?? null),
});
export type PlannedInput = z.input<typeof plannedInput>;

export const plannedPatch = z.object({
  contactId: optId.optional(),
  typeId: z.string().min(1).optional(),
  title: z.string().trim().min(1).max(300).optional(),
  notes: optText.optional(),
  startAt: isoDateTime.optional(),
  durationMinutes: z.coerce.number().int().min(0).max(24 * 60).optional(),
  ownerId: optId.optional(),
  dealId: optId.optional(),
  inviteContact: z.boolean().optional(),
  reminderMinutes: z.coerce.number().int().min(0).max(4 * 7 * 24 * 60).nullable().optional(),
});

/** Marking a planned activity done logs it; anything left out comes from the plan. */
export const plannedCompleteInput = z.object({
  occurredAt: isoDateTime.optional(),
  durationMinutes: z.coerce.number().int().min(0).max(24 * 60).optional(),
  subject: z.string().trim().min(1).max(300).optional(),
  body: optText,
});

/* -------- Accounts -------- */

export const lifecycleMoveInput = z.object({
  stageId: z.string().min(1),
  churnReasonId: optId,
  churnNote: optText,
});
export type LifecycleMoveInput = z.input<typeof lifecycleMoveInput>;

export const onboardingInput = z.object({ done: z.boolean() });

export const flagInput = z.object({
  flagTypeId: z.string().min(1),
  note: optText,
});

export const flagResolveInput = z.object({ resolutionNote: optText });

export const npsInput = z.object({
  contactId: optId,
  score: z.coerce.number().int().min(0).max(10),
  comment: optText,
  respondedAt: isoDate,
});

export const testimonialInput = z.object({
  contactId: optId,
  quote: z.string().trim().min(1).max(5000),
  capturedAt: isoDate,
  approvedForMarketing: z.boolean().default(false),
  usageNotes: optText,
});

export const testimonialPatch = testimonialInput
  .omit({ approvedForMarketing: true })
  .partial()
  .extend({ approvedForMarketing: z.boolean().optional() });

/* -------- Admin -------- */

const color = z.string().regex(/^#[0-9a-fA-F]{6}$/, "Use a hex colour like #2F461A");
const maybeId = z.string().min(1).optional();

export const listItemInput = z.object({
  id: maybeId,
  name: z.string().trim().min(1).max(120),
  color: color.nullish().transform((v) => v ?? null),
  description: optText,
  active: z.boolean().default(true),
});

export const activityTypeInput = z.object({
  id: maybeId,
  name: z.string().trim().min(1).max(80),
  icon: z.string().trim().min(1).max(40).default("circle"),
  direction: z.enum(["outbound", "inbound", "internal"]),
  isOutreach: z.boolean().default(false),
  minDurationMinutes: z.coerce.number().int().min(0).max(600).nullish().transform((v) => v ?? null),
  countsAsContact: z.boolean().default(true),
  nextStepTypeId: z.string().nullish().transform((v) => v || null),
  nextStepTitle: z.string().trim().max(200).nullish().transform((v) => v || null),
  nextStepHours: z.coerce.number().int().min(0).max(24 * 30).nullish().transform((v) => v ?? null),
  active: z.boolean().default(true),
});

export const stageInput = z.object({
  id: maybeId,
  name: z.string().trim().min(1).max(80),
  color,
  active: z.boolean().default(true),
  likelihood: z.coerce.number().int().min(0).max(100),
  isPinchPoint: z.boolean().default(false),
  typicalDays: z.coerce.number().int().min(0).max(365).nullish().transform((v) => v ?? null),
  requiresActivity: z.boolean().default(false),
  requiredActivityTypeId: optId,
  outcome: z.enum(["open", "won", "lost"]),
  forecastCategory: z.enum(["commit", "best_case", "pipeline", "omit"]).nullish().transform((v) => v ?? null),
});

export const lifecycleCategoryInput = z.object({
  id: maybeId,
  name: z.string().trim().min(1).max(80),
  color,
  kind: z.enum(["normal", "at_risk", "offboarding"]),
  active: z.boolean().default(true),
});

export const lifecycleStageInput = z.object({
  id: maybeId,
  categoryId: z.string().min(1),
  name: z.string().trim().min(1).max(80),
  color,
  active: z.boolean().default(true),
});

export const flagTypeInput = z.object({
  id: maybeId,
  name: z.string().trim().min(1).max(80),
  color,
  severity: z.enum(["watch", "at_risk"]),
  active: z.boolean().default(true),
});

export const leadSourceInput = z.object({
  id: maybeId,
  name: z.string().trim().min(1).max(80),
  color: z.string().trim().max(20).nullish().transform((v) => v || null),
  description: z.string().trim().max(300).nullish().transform((v) => v || null),
  needsCampaign: z.boolean().default(false),
  active: z.boolean().default(true),
});

export const reasonInput = z.object({
  id: maybeId,
  name: z.string().trim().min(1).max(120),
  active: z.boolean().default(true),
});

export const segmentTierInput = z.object({
  id: maybeId,
  name: z.string().trim().min(1).max(80),
  minSites: z.coerce.number().int().min(1),
  maxSites: z.coerce.number().int().min(1).nullish().transform((v) => v ?? null),
  color,
});

export const pricingTierInput = z.object({
  id: maybeId,
  label: z.string().trim().min(1).max(80),
  minSites: z.coerce.number().int().min(1),
  maxSites: z.coerce.number().int().min(1).nullish().transform((v) => v ?? null),
  annualRate: z.coerce.number().min(0),
  quarterlyRate: z.coerce.number().min(0),
  annualCode: optShort,
  quarterlyCode: optShort,
});

/** The free periods' £0 Xero items and their usual length. */
const freePeriodSetting = z.object({ code: optShort, months: z.coerce.number().int().min(1, "1 month at least").max(36) });
export const freePeriodsInput = z.object({ pilot: freePeriodSetting, trial: freePeriodSetting, onboarding: freePeriodSetting });

export const productInput = z.object({
  id: maybeId,
  name: z.string().trim().min(1).max(120),
  category: z.string().trim().min(1).max(80).default("Add-on"),
  unitPrice: z.coerce.number().min(0),
  billing: z.enum(["one_off", "annual"]),
  description: optText,
  active: z.boolean().default(true),
});

export const userInput = z.object({
  id: maybeId,
  email: z.string().trim().toLowerCase().email(),
  name: z.string().trim().min(1).max(120),
  role: z.enum(["admin", "member"]),
  active: z.boolean().default(true),
});

export const settingsInput = z.object({
  vatRate: z.coerce.number().min(0).max(1),
  goLiveStepId: z.string().nullable().optional(),
  referralPayWithinDays: z.coerce.number().int().min(0).max(365).optional(),
  defaultFrequency: frequencySchema,
  initialLifecycleStageId: z.string().nullable(),
  bertUsageField: z.string().min(1),
  bertDropThresholdPct: z.coerce.number().min(1).max(100),
  bertWindowWeeks: z.coerce.number().int().min(1).max(12),
  keyPersonLeftDays: z.coerce.number().int().min(1).max(365),
  retentionMonths: z.coerce.number().int().min(1).max(120).optional(),
  mailBackfillDays: z.coerce.number().int().min(0).max(365).optional(),
  mailPreviewChars: z.coerce.number().int().min(500).max(20_000).optional(),
  mailSkipLabel: z.string().trim().min(1).max(100).optional(),
});

/* -------- Import -------- */

export const importMappingSchema = z.object({
  name: z.string(),
  domain: z.string().optional(),
  siteCount: z.string().optional(),
  town: z.string().optional(),
  postcode: z.string().optional(),
  region: z.string().optional(),
  localAuthority: z.string().optional(),
  phone: z.string().optional(),
  website: z.string().optional(),
  contactFirstName: z.string().optional(),
  contactLastName: z.string().optional(),
  contactFullName: z.string().optional(),
  contactEmail: z.string().optional(),
  contactJobTitle: z.string().optional(),
  contactPhone: z.string().optional(),
  /** Partners: one or more partner type names, separated by commas. */
  partnerType: z.string().optional(),
  email: z.string().optional(),
});
export type ImportMapping = z.infer<typeof importMappingSchema>;

export const importInput = z.object({
  csv: z.string().min(1).max(20 * 1024 * 1024),
  mapping: importMappingSchema,
  tagIds: z.array(z.string()).default([]),
  source: z.string().trim().min(1).max(120),
  /** Update matched providers' site count and empty fields. */
  updateExisting: z.boolean().default(false),
  /** Prospects (the customer registry) or partners. */
  registry: z.enum(["customer", "partner"]).default("customer"),
  /** Partners: the pipeline stage new partners start in (the first open stage if not given). */
  stageId: z.string().min(1).optional(),
});
export type ImportInput = z.input<typeof importInput>;

/* ================================================================== */
/* Phase 2                                                             */
/* ================================================================== */

/* -------- Partnerships -------- */

export const partnerInput = z.object({
  name: z.string().trim().min(1, "Name is required").max(200),
  domain: optShort.transform((v) => normaliseDomain(v)),
  website: optShort,
  phone: optShort,
  email: optEmail,
  addressLine: optShort,
  town: optShort,
  postcode: optShort,
  regions: regionList.default([]),
  localAuthority: optShort,
  ownerId: optId,
  notes: optText,
  typeIds: z.array(z.string()).default([]),
  stageId: optId,
});
export type PartnerInput = z.input<typeof partnerInput>;

export const partnerPatch = partnerInput
  .omit({ typeIds: true, stageId: true, regions: true })
  .partial()
  .extend({ typeIds: z.array(z.string()).optional(), regions: regionList.optional() });
export type PartnerPatch = z.input<typeof partnerPatch>;

export const partnerMoveInput = z.object({ stageId: z.string().min(1) });

/* -------- Referrals -------- */

export const rewardItemSchema = z.object({
  kind: z.enum(["free_months", "discount", "voucher", "experience", "commission"]),
  amount: z.coerce.number().min(0).nullish().transform((v) => v ?? null),
  perSite: z.boolean().optional(),
  cap: z.coerce.number().positive().nullish().transform((v) => v ?? null),
  description: z.string().trim().max(300).default(""),
});

export const rewardPackageInput = z.object({
  id: z.string().min(1).optional(),
  name: z.string().trim().min(1).max(120),
  description: optText,
  items: z.array(rewardItemSchema).max(10).default([]),
  active: z.boolean().default(true),
});

export const referralInput = z.object({
  dealId: z.string().min(1),
  referrerOrganisationId: z.string().min(1),
  referrerContactId: optId,
  rewardPackageId: optId,
  notes: optText,
});
export type ReferralInput = z.input<typeof referralInput>;

export const referralPatch = z.object({
  referrerContactId: optId.optional(),
  rewardPackageId: optId.optional(),
  notes: optText.optional(),
});

export const referralStatusInput = z.object({
  status: z.enum(["pending", "issued", "redeemed", "cancelled"]),
});

/* -------- OKR, campaigns, library -------- */

const optDate2 = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, "Use YYYY-MM-DD").nullish().transform((v) => v ?? null);
const reqDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, "Use YYYY-MM-DD");

export const strategicInput = z.object({
  title: z.string().trim().min(1, "Describe the objective").max(300),
  description: optText,
  ownerId: optId,
  horizonStart: optDate2,
  horizonEnd: optDate2,
  status: z.enum(["active", "archived"]).default("active"),
});
export const strategicPatch = strategicInput.omit({ status: true }).partial().extend({ status: z.enum(["active", "archived"]).optional() });

export const tacticalInput = z
  .object({
    strategicId: z.string().min(1),
    title: z.string().trim().min(1, "Name the objective").max(300),
    description: optText,
    ownerId: optId,
    teamId: optId,
    periodStart: reqDate,
    periodEnd: reqDate,
    status: z.enum(["active", "archived"]).default("active"),
  })
  .refine((v) => v.periodEnd >= v.periodStart, { message: "The period must end after it starts.", path: ["periodEnd"] });
export const tacticalPatch = z.object({
  title: z.string().trim().min(1).max(300).optional(),
  description: optText.optional(),
  ownerId: optId.optional(),
  teamId: optId.optional(),
  periodStart: reqDate.optional(),
  periodEnd: reqDate.optional(),
  status: z.enum(["active", "archived"]).optional(),
});

export const keyResultFiltersSchema = z.object({
  campaignId: z.string().nullish().transform((v) => v ?? null),
  segmentIds: z.array(z.string()).optional(),
  ownerId: z.string().nullish().transform((v) => v ?? null),
});

/** How an external key result is fed. Ignored for hub key results. */
const keyResultSourceFields = {
  sourceKind: z.enum(["hub", "manual", "link", "push"]).default("hub"),
  sourceName: optShort,
  unit: z.enum(["count", "sites", "gbp", "percent", "score"]).nullish().transform((v) => v ?? null),
  direction: z.enum(["up", "down"]).nullish().transform((v) => v ?? null),
  valueMode: z.enum(["latest", "sum"]).nullish().transform((v) => v ?? null),
  sourceUrl: z
    .string()
    .trim()
    .max(2000)
    .nullish()
    .transform((v) => v || null)
    .refine((v) => v === null || /^https:\/\//i.test(v), "Use an https:// address"),
  sourceFormat: z.enum(["csv", "json", "text"]).nullish().transform((v) => v ?? null),
  sourceSelector: z
    .string()
    .trim()
    .max(200)
    .nullish()
    .transform((v) => v || null),
  refreshHours: z.coerce.number().int().min(1).max(24 * 7).nullish().transform((v) => v ?? null),
};

export const keyResultInput = z.object({
  tacticalId: z.string().min(1),
  title: z.string().trim().min(1, "Name the key result").max(300),
  /** A hub metric id; external key results use "external" (set for you). */
  metric: z.string().min(1).default("external"),
  filters: keyResultFiltersSchema.default({ campaignId: null, ownerId: null }),
  target: z.coerce.number().positive("Set a target above zero"),
  ...keyResultSourceFields,
});
export const keyResultPatch = z.object({
  title: z.string().trim().min(1).max(300).optional(),
  metric: z.string().min(1).optional(),
  filters: keyResultFiltersSchema.optional(),
  target: z.coerce.number().positive().optional(),
  sourceKind: keyResultSourceFields.sourceKind.unwrap().optional(),
  sourceName: keyResultSourceFields.sourceName.optional(),
  unit: keyResultSourceFields.unit.optional(),
  direction: keyResultSourceFields.direction.optional(),
  valueMode: keyResultSourceFields.valueMode.optional(),
  sourceUrl: keyResultSourceFields.sourceUrl.optional(),
  sourceFormat: keyResultSourceFields.sourceFormat.optional(),
  sourceSelector: keyResultSourceFields.sourceSelector.optional(),
  refreshHours: keyResultSourceFields.refreshHours.optional(),
});

/** A value for an external key result: recorded here, or pushed by another tool. */
export const keyResultValueInput = z.object({
  value: z.coerce.number().finite("Enter a number"),
  /** When it applies; defaults to now. A date or a date and time. */
  at: z.string().datetime({ offset: true }).or(z.string().datetime()).or(isoDate).nullish().transform((v) => v ?? null),
  note: optText,
});

export const campaignInput = z.object({
  name: z.string().trim().min(1, "Name it").max(200),
  kind: z.enum(["campaign", "project"]).default("campaign"),
  description: optText,
  status: z.enum(["planned", "active", "complete", "archived"]).default("active"),
  ownerId: optId,
  startDate: optDate2,
  endDate: optDate2,
  driveUrl: optShort,
  /** Optionally attach it straight away to a tactical objective or key result. */
  tacticalId: optId,
  keyResultId: optId,
});
export const campaignPatch = campaignInput.omit({ kind: true, status: true, tacticalId: true, keyResultId: true }).partial().extend({
  kind: z.enum(["campaign", "project"]).optional(),
  status: z.enum(["planned", "active", "complete", "archived"]).optional(),
});

/** A task, or with parentId a subtask, on a campaign or project. */
export const taskInput = z.object({
  title: z.string().trim().min(1, "Name the task").max(300),
  notes: optText,
  dueOn: optDate,
  assigneeIds: z.array(z.string().min(1)).max(20).default([]),
  parentId: optId,
});
export const taskPatch = taskInput.omit({ parentId: true, assigneeIds: true }).partial().extend({ assigneeIds: z.array(z.string().min(1)).max(20).optional() });
export const taskDoneInput = z.object({ done: z.boolean() });
export const taskMoveInput = z.object({ direction: z.enum(["up", "down"]) });

export const campaignMembersInput = z.object({ organisationIds: z.array(z.string()).min(1).max(5000) });

export const campaignLinkInput = z
  .object({ tacticalId: optId, keyResultId: optId })
  .refine((v) => !!v.tacticalId !== !!v.keyResultId, { message: "Link to a tactical objective or a key result." });

export const assetInput = z.object({
  campaignId: optId,
  kind: z.enum(["pillar", "asset"]).default("asset"),
  name: z.string().trim().min(1, "Name it").max(200),
  url: z.string().trim().url("Paste the full link, starting https://"),
  description: optText,
  tags: z.array(z.string().trim().toLowerCase().min(1).max(40)).max(20).default([]),
});
export const assetPatch = assetInput.omit({ kind: true, tags: true }).partial().extend({ tags: z.array(z.string().trim().toLowerCase().min(1).max(40)).max(20).optional() });

/* -------- Email -------- */

const emailDocument = z.record(z.string(), z.object({ type: z.string(), data: z.record(z.string(), z.unknown()) }));

export const emailSelectionSchema = z.object({
  kind: z.enum(["organisations", "contacts"]),
  ids: z.array(z.string()).min(1, "Select someone to email").max(5000),
  who: z.enum(["all", "key", "topic"]).default("all"),
  source: z.enum(["customers", "prospects", "partners", "contacts"]),
});

export const emailAudienceSchema = z.object({
  selection: emailSelectionSchema.nullish(),
  registry: z.enum(["customer", "partner", "both"]).default("customer"),
  segmentIds: z.array(z.string()).optional(),
  providerTypeIds: z.array(z.string()).optional(),
  tagIds: z.array(z.string()).optional(),
  regions: z.array(z.string()).optional(),
  lifecycleCategoryIds: z.array(z.string()).optional(),
  dealStatus: z.array(z.string()).optional(),
  customersOnly: z.boolean().optional(),
  keyPeopleOnly: z.boolean().optional(),
  campaignId: z.string().nullish(),
});

export const emailTemplateInput = z.object({
  name: z.string().trim().min(1, "Name the template").max(200),
  subject: z.string().trim().min(1, "Add a subject line").max(300),
  preheader: optShort,
  topicId: optId,
  document: emailDocument,
  status: z.enum(["draft", "ready", "archived"]).default("draft"),
});
export const emailTemplatePatch = emailTemplateInput.omit({ status: true }).partial().extend({ status: z.enum(["draft", "ready", "archived"]).optional() });

export const emailSendInput = z.object({
  name: z.string().trim().min(1).max(200),
  templateId: optId,
  subject: z.string().trim().min(1).max(300).optional(),
  preheader: optShort,
  document: emailDocument.optional(),
  topicId: optId,
  audience: emailAudienceSchema.default({ registry: "customer" }),
  channel: z.enum(["mailchimp", "smtp"]).optional(),
});
export const emailSendPatch = z.object({
  name: z.string().trim().min(1).max(200).optional(),
  subject: z.string().trim().min(1).max(300).optional(),
  preheader: optShort.optional(),
  document: emailDocument.optional(),
  topicId: optId.optional(),
  audience: emailAudienceSchema.optional(),
  channel: z.enum(["mailchimp", "smtp"]).optional(),
});

/** Preview an audience before there's a draft (the "Email selected" dialog). */
export const audiencePreviewInput = z.object({
  audience: emailAudienceSchema,
  topicId: optId,
});

export const emailPreviewInput = z.object({
  document: emailDocument,
  subject: z.string().default(""),
  contactId: optId,
});

export const testSendInput = z.object({ email: z.string().trim().toLowerCase().email("Enter a valid email address") });

export const publicPreferencesInput = z.object({
  topicIds: z.array(z.string()).default([]),
  unsubscribeAll: z.boolean().default(false),
});

/** Sender events, normalised. Each live sender adapter maps its own webhook payload to this. */
export const emailEventsInput = z.object({
  events: z
    .array(
      z.object({
        messageId: z.string().min(1),
        type: z.enum(["delivered", "bounce", "complaint", "open", "click", "unsubscribe"]),
        bounceType: z.enum(["hard", "soft"]).optional(),
        at: z.string().datetime({ offset: true }).optional(),
      }),
    )
    .max(1000),
});

export const emailSettingsInput = z.object({
  fromName: z.string().trim().min(1).max(100),
  fromEmail: z.string().trim().toLowerCase().email(),
  replyTo: z.string().trim().toLowerCase().email().nullish().transform((v) => v || null),
  sendingDomain: z.string().trim().toLowerCase().min(3).max(200),
  dkimSelector: z.string().trim().min(1).max(100),
  postalAddress: z.string().trim().min(1, "UK marketing emails need a postal address").max(300),
  defaultChannel: z.enum(["mailchimp", "smtp"]),
  warmupStart: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).nullable(),
  warmupStartVolume: z.coerce.number().int().min(1).max(100000),
  warmupDailyIncreasePct: z.coerce.number().min(0).max(200),
  warmupMaxDaily: z.coerce.number().int().min(1).max(10_000_000),
});

/* -------- Insights -------- */

export const insightQuerySchema = z.object({
  dataset: z.enum(["providers", "partners", "deals", "accounts", "activities", "referrals", "key_results", "deliveries"]),
  measure: z.string().regex(/^(count|(sum|avg):[a-z_]+)$/),
  groupBy: z.string().nullish(),
  splitBy: z.string().nullish(),
  filters: z.record(z.string(), z.array(z.string())).optional(),
  lastMonths: z.coerce.number().int().min(1).max(60).nullish(),
  sort: z.enum(["value", "label"]).optional(),
  limit: z.coerce.number().int().min(1).max(100).optional(),
});

export const dashboardWidgetSchema = z.object({
  id: z.string().min(1),
  title: z.string().trim().min(1).max(120),
  type: z.enum(["stat", "bar", "line", "pie", "table", "region_map"]),
  query: insightQuerySchema,
  width: z.union([z.literal(1), z.literal(2), z.literal(3)]).default(1),
});

export const dashboardInput = z.object({
  name: z.string().trim().min(1, "Name the dashboard").max(120),
  description: optText,
  shared: z.boolean().default(true),
  widgets: z.array(dashboardWidgetSchema).max(40).default([]),
});
export const dashboardPatch = dashboardInput.omit({ shared: true, widgets: true }).partial().extend({
  shared: z.boolean().optional(),
  widgets: z.array(dashboardWidgetSchema).max(40).optional(),
});

/* -------- Google Calendar -------- */

export const calendarDirectionInput = z.object({ direction: z.enum(["two_way", "push_only"]) });

/* -------- Data export and subject access requests -------- */

export const exportRequestSchema = z.object({
  dataset: z.string().min(1),
  /** Column keys in order; empty = every column. */
  columns: z.array(z.string()).default([]),
  filters: z.record(z.string(), z.any()).default({}),
});

export const dataRequestInput = z.object({
  contactId: z.string().min(1, "Pick the person"),
  receivedOn: isoDate,
  notes: optText,
});
export const dataRequestPatch = z.object({
  status: z.enum(["open", "completed"]).optional(),
  dueOn: isoDate.optional(),
  notes: optText.optional(),
});

/* -------- Data retention -------- */

/** A retention review decision. Keep restarts the clock; anonymise and delete can't be undone. */
export const retentionDecisionInput = z.object({
  action: z.enum(["keep", "anonymise", "delete"]),
  note: optText,
});

/* -------- Tracked links -------- */

export const trackedLinkInput = z
  .object({
    label: z.string().trim().min(2, "At least 2 characters").max(80),
    channel: z.enum(LINK_CHANNELS, { message: "Pick a channel" }),
    /** A hub campaign; its name becomes the campaign tag. Or name a campaign that isn't in the hub. */
    campaignId: optId,
    campaign: z.string().trim().max(80).nullish().transform((v) => (v ? v : null)),
    destinationUrl: z
      .string()
      .trim()
      .nullish()
      .transform((v) => v || LINK_FALLBACK_URL),
    /** Admins only: a destination off get-otii.com. Audited. */
    destinationOverride: z.boolean().default(false),
    utmContent: z.string().trim().max(80).nullish().transform((v) => (v ? v : null)),
    expiresOn: optDate,
    recipientTracking: z.boolean().default(false),
  })
  .refine((v) => !!v.campaignId || (v.campaign?.length ?? 0) >= 2, { path: ["campaign"], message: "Pick a campaign or name one" })
  .superRefine(checkDestination)
  .transform((v) => ({ ...v, destinationUrl: canonicalDestination(v.destinationUrl) }));

/** get-otii.com, unless an admin overrides it; even then https only, with no login in the address. */
function checkDestination(v: { destinationUrl: string; destinationOverride: boolean }, ctx: z.RefinementCtx) {
  if (isAllowedDestination(v.destinationUrl)) return;
  let ok = false;
  try {
    const u = new URL(v.destinationUrl);
    ok = u.protocol === "https:" && !u.username && !u.password;
  } catch {
    ok = false;
  }
  if (!ok) ctx.addIssue({ code: "custom", path: ["destinationUrl"], message: "Use an https address" });
  else if (!v.destinationOverride) ctx.addIssue({ code: "custom", path: ["destinationUrl"], message: "Use an https address on get-otii.com, or tick the override" });
}

/** A new destination, any time: printed links keep their slug and change only where they go. */
export const linkDestinationInput = z
  .object({ destinationUrl: z.string().trim().min(1, "Give the destination"), destinationOverride: z.boolean().default(false) })
  .superRefine(checkDestination)
  .transform((v) => ({ ...v, destinationUrl: canonicalDestination(v.destinationUrl) }));
/** Marking a link printed is permanent: the slug can never go. */
export const linkPrintedInput = z.object({ printed: z.literal(true), confirm: z.literal(true, { message: "Confirm the link is printed" }) });
/** QR export: format and the optional centred logo. */
export const linkQrQuery = z.object({ format: z.enum(["svg", "png"]).default("svg"), logo: z.enum(["0", "1"]).default("0") });

export const linkActiveInput = z.object({ active: z.boolean() });
export const linkRecipientInput = z.object({ contactId: z.string().min(1, "Pick the person") });
